<template>
	<div :class="[wrapperClass, cover ? COVER_BOX : '']">
		<iframe
			:src="embedSrc"
			:title="title || $t('Play video')"
			:class="cover ? COVER_FRAME : 'w-full h-full'"
			frameborder="0"
			scrolling="no"
			:allow="allow"
			allowfullscreen
		/>
	</div>
</template>

<script setup lang="ts">
const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";

const DEFAULT_BOX =
	"w-full aspect-[9/16] md:aspect-video md:h-[var(--desktop-height,auto)]";

const COVER_BOX = "relative overflow-hidden md:[container-type:size]";
const COVER_FRAME =
	"w-full h-full md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[calc(100cqh*16/9)] md:h-[100cqh] md:min-w-[100cqw] md:min-h-[calc(100cqw*9/16)]";

const BASE_PERMISSIONS = [
	"accelerometer",
	"encrypted-media",
	"fullscreen",
	"picture-in-picture"
];

interface Props {
	src?: string;
	srcMobile?: string;
	title?: string;
	autoplay?: boolean;
	cover?: boolean;
	wrapperClass?: string;
}
const {
	src,
	srcMobile,
	title,
	autoplay = false,
	cover = false,
	wrapperClass = DEFAULT_BOX
} = defineProps<Props>();

const allow = computed(() =>
	(autoplay ? ["autoplay", ...BASE_PERMISSIONS] : BASE_PERMISSIONS).join("; ")
);

const hasMobileSrc = computed(() => !!srcMobile && srcMobile !== src);

// `null` until the viewport has been measured on the client.
const isMobileViewport = ref<boolean | null>(null);
let viewport: MediaQueryList | null = null;

const handleViewportChange = (event: MediaQueryListEvent) => {
	isMobileViewport.value = event.matches;
};

onMounted(() => {
	viewport = window.matchMedia(MOBILE_VIEWPORT_QUERY);
	isMobileViewport.value = viewport.matches;
	viewport.addEventListener("change", handleViewportChange);
});

onBeforeUnmount(() => {
	viewport?.removeEventListener("change", handleViewportChange);
});

// An iframe carries a single src, so a separate mobile embed can only be picked
// once the viewport is known. Until then the src is withheld rather than
// rendering the desktop embed and swapping it, which would load two documents
// on mobile. Nothing is lost server-side — an iframe never loads during SSR.
const embedSrc = computed(() => {
	if (!hasMobileSrc.value) return src;
	if (isMobileViewport.value === null) return undefined;
	return isMobileViewport.value ? srcMobile : src;
});
</script>
